// KbGuidePage — fetches a guide content row and renders via PageShell +
// GuideMarkdown. Visual identical to a hand-rolled GUIDE_*.tsx.

import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";
import { PageShell, GuideMarkdown } from "@/components/page";
import type { KbGuideContent } from "./types";
import { resolveIcon } from "./iconMap";

export function KbGuidePage({ slug }: { slug: string }) {
  const api = useApi();

  const { data, isLoading, error } = useQuery({
    queryKey: ["kb", slug],
    queryFn: async (): Promise<KbGuideContent> => {
      const res = await api(`/api/kb/${encodeURIComponent(slug)}`);
      if (!res.ok) {
        throw new Error(`Failed to load /api/kb/${slug}: HTTP ${res.status}`);
      }
      const json = (await res.json()) as { content: KbGuideContent };
      return json.content;
    },
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div dir="rtl" className="flex items-center gap-2 p-8 text-sm text-stone-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        טוען…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div dir="rtl" className="p-8">
        <div className="rounded-2xl border border-rose-300/60 bg-rose-50/40 p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          <div className="text-sm font-semibold text-rose-700">לא ניתן לטעון את המדריך</div>
          <div className="mt-1 text-xs text-stone-500">
            Slug: <code className="rounded bg-stone-100 px-1.5 py-0.5 font-mono">{slug}</code>
          </div>
          {error && (
            <div className="mt-2 text-xs text-rose-600/80">{(error as Error).message}</div>
          )}
        </div>
      </div>
    );
  }

  const Icon = resolveIcon(data.iconName);

  return (
    <div dir="rtl">
      <PageShell
        icon={Icon}
        iconColor={data.iconColor ?? "violet"}
        title={data.title}
        subtitle={data.subtitle}
      >
        {data.sections.map((section, idx) => (
          <div key={idx} className="space-y-4">
            <GuideMarkdown body={section.body} />
            {section.source && (
              <p className="text-[11px] text-muted-foreground/70 tracking-wide">
                מקור: {section.source}
              </p>
            )}
          </div>
        ))}
      </PageShell>
    </div>
  );
}
